import React, { useState, useRef, useEffect } from "react";
import {
  FaCircle,
  FaBars,
  FaHome,
  FaClipboardList,
  FaGraduationCap,
  FaChartLine,
  FaUser,
} from "react-icons/fa";
import { Link, useNavigate, useLocation } from "react-router-dom";
import profile from "../images/profile.png";
import logo from "../images/moh-logo.png";
import apiRequest from "../services/api";

const Header = ({ onUserLogin, isSignedIn, user }) => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const profileRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation(); // To track active link

  const logout = async () => {
    try {
      const token = localStorage.getItem("token");
      if (token) await apiRequest("auth/logout", { method: "POST" });
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setShowProfileMenu(false);
      onUserLogin(false);
      navigate("/");
    } catch (error) {
      console.error("Logout failed", error);
      localStorage.clear();
      onUserLogin(false);
      navigate("/");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target))
        setShowProfileMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Bottom Navigation Config
  const navItems = [
    { label: "Home", path: "/", icon: <FaHome size={20} /> },
    { label: "Apply", path: "/apply", icon: <FaClipboardList size={20} /> },
    { label: "Results", path: "/results", icon: <FaGraduationCap size={20} /> },
  ];

  return (
    <>
      <header className="sticky top-0 z-50 bg-white shadow-md border-b">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 py-3">
          {/* LEFT: LOGO */}
          <Link to="/" className="flex items-center gap-2 group">
            <img
              src={logo}
              alt="HPLE Logo"
              className="h-10 md:h-14 object-contain"
            />
            <div className="flex flex-col">
              <span className="text-blue-900 text-lg md:text-xl font-black font-serif leading-none tracking-tight">
                EHPLE
              </span>
              <span className="hidden md:block text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                Ethiopian Health Professionals Licensing Examination
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6 font-medium text-gray-700">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`hover:text-blue-500 transition-colors ${
                  location.pathname === item.path ? "text-blue-600" : ""
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-4">
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="focus:outline-none relative"
              >
                <img
                  src={profile}
                  alt="Profile"
                  className="w-9 h-9 rounded-full border-2 border-blue-500 cursor-pointer"
                />
                <FaCircle
                  className="absolute bottom-0 right-0 text-blue-500 border-2 border-white rounded-full"
                  size={10}
                />
              </button>

              {showProfileMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b bg-gray-50 md:hidden">
                    <p className="text-sm font-bold truncate text-gray-800">
                      {user?.name || "Candidate"}
                    </p>
                  </div>
                  <ul className="py-1 text-sm text-gray-700">
                    <li
                      className="px-4 py-2 hover:bg-blue-50 cursor-pointer flex items-center gap-2"
                      onClick={() => {
                        navigate("/profile");
                        setShowProfileMenu(false);
                      }}
                    >
                      <FaUser size={12} /> View Profile
                    </li>
                    <li
                      className="px-4 py-2 text-red-600 hover:bg-red-50 cursor-pointer font-bold border-t"
                      onClick={logout}
                    >
                      Sign out
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* MOBILE BOTTOM MENU */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 flex justify-around items-center py-2 px-1 shadow-[0_-2px_10px_rgba(0,0,0,0.05)] safe-area-inset-bottom">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center flex-1 py-1 gap-1 transition-all ${
                isActive ? "text-blue-600" : "text-gray-400"
              }`}
            >
              <div
                className={`transition-transform ${
                  isActive ? "scale-110" : ""
                }`}
              >
                {item.icon}
              </div>
              <span className="text-[10px] font-bold tracking-tight leading-none">
                {item.label}
              </span>
              {isActive && (
                <div className="absolute bottom-0 w-8 h-1 bg-blue-600 rounded-t-full" />
              )}
            </Link>
          );
        })}
      </nav>

      {/* Spacer for bottom nav so content isn't hidden */}
      <div className="h-16 md:hidden" />
    </>
  );
};

export default Header;
